import { STORAGE_BASE_URL } from '../config/api';

// Absolute URLs (http/https, blob, data) and root-relative/same-origin asset
// paths (e.g. "/assets/..." served by the local build) are direct references
// and must never be wrapped in the blob storage base URL or a SAS token.
const DIRECT_URL_REGEX = /^(https?:|blob:|data:)/i;
const ROOT_RELATIVE_REGEX = /^[/.]+/;

const BLOB_STORAGE_URL_REGEX =
  /^https?:\/\/[^/]*blob\.core\.windows\.net\/[^?]*/i;

const appendQueryParams = (url: string, query: string): string => {
  if (!query) return url;

  const normalizedQuery = query.replace(/^\?/, '');
  if (!normalizedQuery) return url;

  return `${url}${url.includes('?') ? '&' : '?'}${normalizedQuery}`;
};

/**
 * Builds a playable download URL for a stored blob asset.
 *
 * The document/backend stores relative blob paths (not signed URLs). The SAS
 * token is returned alongside each video document, so the frontend combines
 * the relative path with the SAS token to render content:
 *
 *   <STORAGE_BASE_URL>/<relativePath>?<sasToken>
 *
 * Direct references — absolute URLs (e.g. the temporary generated video URL
 * before upload) and root-relative/same-origin asset paths (e.g. local build
 * assets like "/assets/Christmas_01-01.png") — are returned unchanged and are
 * never re-signed.
 *
 * @param pathOrUrl relative blob path or a direct (absolute/root-relative) URL
 * @param sasToken  SAS token query string (optionally with a leading '?')
 */
export const getAssetDownloadUrl = (
  pathOrUrl?: string | null,
  sasToken?: string | null
): string => {
  if (!pathOrUrl) return '';

  // Blob-hosted absolute URLs still need SAS signing when the asset is stored
  // in private storage. Root-relative and non-blob direct URLs remain direct.
  if (DIRECT_URL_REGEX.test(pathOrUrl) || ROOT_RELATIVE_REGEX.test(pathOrUrl)) {
    if (sasToken && BLOB_STORAGE_URL_REGEX.test(pathOrUrl)) {
      return appendQueryParams(pathOrUrl, sasToken);
    }

    return pathOrUrl;
  }

  // Otherwise treat the value as a blob-stored relative path.
  const normalizedPath = pathOrUrl.replace(/^\/+/, '');
  let url = `${STORAGE_BASE_URL}${normalizedPath}`;

  if (sasToken) {
    url = appendQueryParams(url, sasToken);
  }

  return url;
};

// Default lead time before a SAS token expires at which we treat it as needing
// a refresh (avoids letting the token die mid-render/playback).
const DEFAULT_REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Whether the video's SAS token is missing, already expired, or within
 * `bufferMs` of expiring and therefore should be refreshed.
 *
 * @param video    the video document with optional `sas: { token, expiredAt? }`
 * @param bufferMs lead time (ms) before expiry to flag a refresh; defaults to
 *                 5 minutes. Passing 0 flags only at/after expiry.
 */
export const isSasTokenExpiring = (
  video?: { sas?: { token?: string; expiredAt?: number } } | null,
  bufferMs: number = DEFAULT_REFRESH_BUFFER_MS
): boolean => {
  const sas = video?.sas;
  if (!sas?.token || typeof sas.expiredAt !== 'number') {
    // No usable token (or it has no expiry) -> a refresh is required.
    return true;
  }

  return Date.now() >= sas.expiredAt - bufferMs;
};

// Video container extensions accepted by the upload pipeline (see
// uploadedAssetStorageService.ts VIDEO_EXTENSIONS). Centralised here so every
// consumer (scene menu thumbnails, video management previews) detects the same
// set of URLs as video — including SAS-signed URLs, where the extension must
// appear before the query string or fragment.
const VIDEO_MEDIA_URL_REGEX =
  /\.(?:mp4|m4v|mov|webm|mkv|avi|wmv|flv|mpg|mpeg|mpe|3gp|3g2|ts|m2ts|ogv|f4v)(?:\?|#|$)/i;

/**
 * Whether a resolved asset URL points at a video file (by container
 * extension). Safe to call with SAS-signed URLs: the extension must appear
 * before the query string or fragment.
 */
export const isVideoMediaUrl = (url?: string | null): boolean =>
  typeof url === 'string' && VIDEO_MEDIA_URL_REGEX.test(url);
